import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormControl,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpService } from '../../../services/http.service';
import { CustomToastComponent } from '../../toast/custom-toast.component';
import { finalize } from 'rxjs';

interface EmailTemplateOptions {
  logoUrl?: string;
  footerLogoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  buttonText?: string;
  buttonUrl?: string;
  highlightBgColor?: string;
  eventSummaryBgColor?: string;
  eventLink?: string;
  eventName?: string;
  eventManager?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  eventTime?: string;
  location?: string;
  eventTypeName?: string;
  managers?: any[];
}

@Component({
  selector: 'app-event-copy-link',
  standalone: true,
  templateUrl: './event-copy-link-dialog.component.html',
  styleUrls: ['./event-copy-link-dialog.component.scss'],
  imports: [CommonModule, ReactiveFormsModule],
})
export class EventLinkDialogComponent implements OnInit {
  @ViewChild('emailEditor') emailEditor!: ElementRef<HTMLDivElement>;
  form: FormGroup;
  loading = false;
  generatedEventLink: string = '';
  eventData: any;
  eventId: string = '';
  schoolId: string = '';

  emailsList: string[] = [];
  emailInputControl = new FormControl('', [Validators.email]);
  showEmailError = false;
  emailErrorMessage = '';
  studentShare: boolean = false;

  get emailPreviewControl(): FormControl {
    return this.form.get('emailPreview') as FormControl;
  }
  loginRole: any = '';

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<EventLinkDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      eventId: string;
      initialData: any;
      eventData: any;
      studentShare: boolean;
    },
  ) {
    this.form = this.fb.group({
      eventLink: ['', Validators.required],
      ccEmail: [''],
      email: ['', [Validators.required]],
      emailPreview: [''],
    });
  }

  ngOnInit(): void {
    const role = localStorage.getItem('userRole');
    this.loginRole = role;
    const userDetailsString = localStorage.getItem('userDetails');
    if (userDetailsString) {
      const userDetails = JSON.parse(userDetailsString);
      this.schoolId = userDetails.schoolId;
    }
    this.eventData = this.data.eventData || this.data;
    this.studentShare = this.data.studentShare;
    this.eventId = this.data?.eventId || '';
    if (this.loginRole === 'teacher') {
      this.getStudentEventLink();
    } else {
      this.getTeacherEventLink();
    }
    this.emailInputControl.valueChanges.subscribe(() => {
      if (this.showEmailError) {
        this.clearEmailError();
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeEmailTemplate();
  }

  initializeEmailTemplate() {
    const composeMailContent = this.generateComposeMailContent();
    this.emailPreviewControl.setValue(composeMailContent);
    setTimeout(() => {
      if (this.emailEditor) {
        this.emailEditor.nativeElement.innerHTML = composeMailContent;
      }
    }, 0);
  }

  generateComposeMailContent(): string {
    const eventData = this.eventData;
    if (!eventData) {
      return this.getFallbackTemplate();
    }

    const managerFirstName = eventData.managers?.[0]?.firstName || '';
    const managerLastName = eventData.managers?.[0]?.lastName || '';
    const eventManager =
      `${managerFirstName} ${managerLastName}`.trim() || 'Event Manager';

    const options: EmailTemplateOptions = {
      eventName: eventData.eventName || 'Event',
      eventManager: eventManager,
      eventStartDate: eventData.eventStartDate,
      eventEndDate: eventData.eventEndDate,
      eventTime: eventData.eventTime || 'Time not set',
      location: eventData.location || 'Location not set',
      eventTypeName: eventData.eventTypeName || 'Program',
      managers: eventData.managers || [],
      eventLink: this.generatedEventLink,
    };

    if (this.studentShare) {
      return this.getStudentShareTemplate(options);
    }

    return this.getFallbackTemplate(options);
  }

  getStudentShareTemplate(options: EmailTemplateOptions = {}): string {
    const baseUrl = this.httpService.getApiUrl().replace('/api', '');
    const {
      logoUrl = `${baseUrl}/assets/images/email-temp-logo.png`,
      footerLogoUrl = `${baseUrl}/assets/images/email-temp-image.png`,
      eventStartDate = '',
      eventEndDate = '',
      eventTime = 'Time not set',
      location = 'Location not set',
      eventTypeName = 'Program',
      eventLink = '#',
    } = options;

    let formattedStartDate = 'Date not set';
    if (eventStartDate) {
      const startDate = new Date(eventStartDate);
      formattedStartDate = startDate.toLocaleDateString('en-GB');
    }
    let formattedEndDate = '';
    if (eventEndDate) {
      const endDate = new Date(eventEndDate);
      formattedEndDate = endDate.toLocaleDateString('en-GB');
    }
    let dateDisplay = formattedStartDate;
    if (formattedEndDate && formattedEndDate !== formattedStartDate) {
      dateDisplay = `${formattedStartDate} - ${formattedEndDate}`;
    }

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=yes">
    <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap" rel="stylesheet">
</head>
<body style="margin:0; padding:0; background-color:#ffffff; font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; line-height:1.5;">
    <table width="100%" cellpadding="0" cellspacing="0" border="0" align="center" bgcolor="#ffffff" role="presentation" style="background-color:#ffffff;">
        <tr>
            <td align="center" bgcolor="unset" style="background-color:unset;">
                <table width="100%" cellpadding="0" cellspacing="0" border="0" align="center" style="max-width:640px; width:100%; background-color:#000000; border-radius:10px;" role="presentation">
                    <tr>
                        <td align="center" style="border-radius:10px;">
                            <div style="max-width:640px; margin:0 auto; background-color:#ffffff;">
                                <div style="background-color:#ffffff; border-radius:10px; color:#000000; padding:24px 28px; text-align:left; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                    <div style="margin: 12px 0 8px 0;">
                                        <img src="${this.escapeHtml(logoUrl)}" style="display:block; width:100%; max-width:600px; height:auto; border-radius:6px" />
                                    </div>
                                    <div style="font-size:16px; color:#000000; margin-bottom:12px;">Hi!</div>
                                    <div style="margin: 8px 0 12px 0;">
                                        <p style="margin:0; color:#000000;">You've been invited to attend an upcoming event.</p>
                                    </div>
                                    <div style="margin: 12px 0 6px 0;">
                                        <strong style="font-size:16px; color:#000000;">📅 Event Summary</strong>
                                    </div>
                                    <div style="padding-left: 2px; margin-bottom: 12px;">
                                        <div style="margin: 6px 0; font-size:15px; color:#000000;"><strong style="color:#000000;">📅 When:</strong> ${this.escapeHtml(dateDisplay)}</div>
                                        <div style="margin: 6px 0; font-size:15px; color:#000000;"><strong style="color:#000000;">⏰ Time:</strong> ${this.escapeHtml(eventTime)}</div>
                                        <div style="margin: 6px 0; font-size:15px; color:#000000;"><strong style="color:#000000;">📍 Where:</strong> ${this.escapeHtml(location)}</div>
                                        <div style="margin: 6px 0; font-size:15px; color:#000000;"><strong style="color:#000000;">🎒 What's On:</strong> ${this.escapeHtml(eventTypeName)}</div>
                                    </div>
                                    <div style="margin: 8px 0 12px 0;">
                                        <p style="margin:0; color:#000000;">To take part, you'll need to register an account in the CSQ Construction Pathways Experience Portal.</p>
                                    </div>
                                    <div style="margin: 8px 0 16px 0;">
                                        <span style="font-size:15px;">👉</span> <a href="${this.escapeHtml(eventLink)}" target="_blank" style="display:inline-block; background-color:#FFE033; color:#000000; font-weight:bold; padding:12px 24px; border-radius:8px; text-decoration:none; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size:15px;">Please register/log into your account to get started</a>
                                    </div>
                                    <div style="margin: 8px 0 12px 0;">
                                        <p style="margin:0; color:#000000;">If you are registering an account - you will need to have your parent/guardian's email handy as they need to approve the account being created before you get log in details.</p>
                                    </div>
                                    <div style="margin: 8px 0 12px 0;">
                                        <p style="margin:0; color:#000000;">If you have any trouble signing up or logging in, let me know. <br> Thanks,</p>
                                    </div>                                                                   
                                </div>
                            </div>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
  }

  getFallbackTemplate(options: EmailTemplateOptions = {}): string {
    const baseUrl = this.httpService.getApiUrl().replace('/api', '');
    const {
      logoUrl = `${baseUrl}/assets/images/email-temp-logo.png`,
      footerLogoUrl = `${baseUrl}/assets/images/email-temp-image.png`,
      eventName = 'Event',
      eventManager = 'Event Manager',
      eventStartDate = '',
      eventEndDate = '',
      eventTime = 'Time not set',
      location = 'Location not set',
      eventTypeName = 'Program',
      managers = [],
      eventLink = '#',
    } = options;

    let formattedStartDate = 'Date not set';
    if (eventStartDate) {
      const startDate = new Date(eventStartDate);
      formattedStartDate = startDate.toLocaleDateString('en-GB');
    }
    let formattedEndDate = '';
    if (eventEndDate) {
      const endDate = new Date(eventEndDate);
      formattedEndDate = endDate.toLocaleDateString('en-GB');
    }
    let dateDisplay = formattedStartDate;
    if (formattedEndDate && formattedEndDate !== formattedStartDate) {
      dateDisplay = `${formattedStartDate} - ${formattedEndDate}`;
    }

    const managerContact = managers
      .map((opt: any) =>
        opt?.email
          ? `<a href="mailto:${opt.email}" target="_blank">${opt.email}</a>`
          : null,
      )
      .filter(Boolean);

    const contactNames = managerContact.length
      ? managerContact.join(', ')
      : 'CSQ';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=yes">
    <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap" rel="stylesheet">
</head>
<body style="margin:0; padding:0; background-color:#ffffff; font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; line-height:1.5;">
    <table width="100%" cellpadding="0" cellspacing="0" border="0" align="center" bgcolor="#ffffff" role="presentation" style="background-color:#ffffff;">
        <tr>
            <td align="center" bgcolor="unset" style="background-color:unset;">
                <table width="100%" cellpadding="0" cellspacing="0" border="0" align="center" style="max-width:640px; width:100%; background-color:#ffffff; border-radius:10px;" role="presentation">
                    <tr>
                        <td align="center" style="border-radius:10px;">
                            <div style="max-width:640px; margin:0 auto; background-color:#ffffff;">
                                <div style="background-color:#ffffff; border-radius:10px; color:#ffffff; padding:24px 28px; text-align:left; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                    <div style="margin: 12px 0 8px 0;">
                                        <img src="${this.escapeHtml(logoUrl)}" style="display:block; width:100%; max-width:600px; height:auto; border-radius:6px" />
                                    </div>
                                    <div style="font-size:16px; color:#000000; margin-bottom:12px; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Hi,</div>
                                    <div style="margin: 8px 0 12px 0; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        <p style="margin:0; color:#000000;">Your event <strong>${eventName}</strong> has been successfully created in our Construction Pathways Experience Portal, by <strong>${eventManager}</strong> – exciting! 🎉</p>
                                    </div>
                                    <div style="margin: 8px 0 6px 0; font-size:15px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        Here's a quick snapshot of the key details:
                                    </div>
                                    <div style="margin: 12px 0 6px 0;">
                                        <strong style="font-size:16px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Event Summary</strong>
                                    </div>
                                   <table width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
  <tr>
    <td style="padding-left: 2px; padding-bottom: 12px;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
        <!-- 📅 When -->
        <tr>
          <td style="margin: 0; padding: 6px 0; font-size: 15px; color: #000000; font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
            <strong style="color: #000000;">📅 When:</strong> ${this.escapeHtml(dateDisplay)}
          </td>
        </tr>
        
        <!-- ⏰ Time -->
        <tr>
          <td style="margin: 0; padding: 6px 0; font-size: 15px; color: #000000; font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
            <strong style="color: #000000;">⏰ Time:</strong> ${this.escapeHtml(eventTime)}
          </td>
        </tr>
        
        <!-- 📍 Where -->
        <tr>
          <td style="margin: 0; padding: 6px 0; font-size: 15px; color: #000000; font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
            <strong style="color: #000000;">📍 Where:</strong> ${this.escapeHtml(location)}
          </td>
        </tr>
        
        <!-- 🎒 What's On -->
        <tr>
          <td style="margin: 0; padding: 6px 0; font-size: 15px; color: #000000; font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
            <strong style="color: #000000;">🎒 What's On:</strong> ${this.escapeHtml(eventTypeName)}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
                                    <div style="margin: 8px 0 16px 0; font-size:15px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        <strong style="color:#000000;">Note:</strong> ${this.escapeHtml(eventManager)} will notify you on any updates/changes for your event.
                                    </div>
                                    <div style="margin: 12px 0 8px 0;">
                                        <p style="margin:0 0 6px 0; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;"><strong style="color:#000000;">🔗 Manage Your Event</strong></p>
                                        <p style="margin:0 0 10px 0; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">You can view the event details or begin managing the students attending your event here:</p>
                                        <span style="font-size:15px;">👉</span> <a href="${this.escapeHtml(eventLink)}" target="_blank" style="display:inline-block; background-color:#FFE033; color:#000000; font-weight:bold; padding:10px 20px; border-radius:6px; text-decoration:none; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size:15px;">Event Link</a>
                                    </div>
                                    <div style="background-color: #fff2e1; padding: 18px 20px; border-radius: 12px; margin: 20px 0; border: 1px solid #FAA633;">
                                        <p style="margin:0 0 10px 0; font-weight:bold; font-size:15px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Options for registering Student to the Portal?</p>
                                        <ul style="margin:0 0 4px 0; padding-left:20px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                            <li style="margin-bottom:10px; line-height:1.45; color:#000000;">You can bulk upload your students via the student management page – download the CSV and upload them all in at once – manually add students to each event via event page. OR,</li>
                                            <li style="margin-bottom:10px; line-height:1.45; color:#000000;">Use the "Share Event" button on the event page to copy an email template to help with EOI to multiple students. Your students can register themselves for both the portal and event! OR,</li>
                                            <li style="margin-bottom:4px; line-height:1.45; color:#000000;">Add students against the event directly via the event page above by clicking the "assign student" button.</li>
                                        </ul>
                                    </div>
                                    <div style="margin: 8px 0 4px 0;">
                                        <strong style="color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">Parent Consent for Student Portal Access?</strong>
                                    </div>
                                    <div style="margin: 8px 0 8px 0; font-size:15px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        Parental consent is always required for our portal to have a student receive access.
                                    </div>
                                    <div style="margin: 12px 0 8px 0;">
                                        <img style="width:100%" src="${this.escapeHtml(footerLogoUrl)}" />
                                    </div>
                                    <div style="margin: 6px 0 8px 0; font-size:15px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        Any students can still attend Construction Pathways events even if consent hasn't been completed or parent has declined an account being created!
                                    </div>
                                    <div style="margin: 12px 0 8px 0; font-size:15px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        If you receive any questions, helpful information is available in the "Get Started Guide" under the FAQs section of the portal.
                                    </div>
                                    <div style="margin: 18px 0 4px 0;">
                                        <strong style="color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">🛠️ Need Help?</strong>
                                    </div>
                                    <div style="margin: 6px 0 12px 0; font-size:15px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        If you need to adjust details or require support, contact us anytime by reaching out to <strong style="color:#000000;"> ${this.escapeHtml(eventManager)}</strong> or email us at <a href="mailto:constructionpathways@csq.org.au" style="color:#2CC3E8;">constructionpathways@csq.org.au</a>
                                    </div>
                                    <div style="margin: 24px 0 0 0; border-top: 1px solid #2a2a2a; padding-top: 16px; color:#000000; font-family:'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                                        Thank you,<br/>
                                        <strong style="color:#000000;">CSQ - Construction Pathways Team</strong>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  getTeacherEventLink() {
    const eventID = this.eventId;

    if (!eventID) {
      console.error('No event ID found');
      this.initializeEmailTemplate();
      return;
    }

    this.httpService
      .get<any>(`events/teacher/${eventID}/gen-link`)
      .pipe(finalize(() => {}))
      .subscribe({
        next: (response) => {
          if (response?.status && response?.data?.gen_Link) {
            this.generatedEventLink = response.data.gen_Link;
            this.form.patchValue({ eventLink: this.generatedEventLink });
            this.updateEmailTemplateWithLink(this.generatedEventLink);
          }
        },
        error: (err) => {
          console.error('Error fetching event link:', err);
          this.showErrorToast(err?.message || 'Failed to get event link');
          this.initializeEmailTemplate();
        },
      });
  }

  getStudentEventLink() {
    const eventID = this.eventId;
    const schoolId = this.schoolId;

    if (!eventID) {
      console.error('No event ID found');
      this.initializeEmailTemplate();
      return;
    }

    this.httpService
      .get<any>(`teacherstudent/students/${eventID}/${schoolId}/gen-link`)
      .pipe(finalize(() => {}))
      .subscribe({
        next: (response) => {
          if (response?.status && response?.data?.gen_Link) {
            this.generatedEventLink = response.data.gen_Link;
            this.form.patchValue({ eventLink: this.generatedEventLink });
            this.updateEmailTemplateWithLink(this.generatedEventLink);
          }
        },
        error: (err) => {
          console.error('Error fetching event link:', err);
          this.showErrorToast(err?.message || 'Failed to get event link');
          this.initializeEmailTemplate();
        },
      });
  }

  updateEmailTemplateWithLink(eventLink: string) {
    if (this.eventData) {
      const managerFirstName = this.eventData.managers?.[0]?.firstName || '';
      const managerLastName = this.eventData.managers?.[0]?.lastName || '';
      const eventManager =
        `${managerFirstName} ${managerLastName}`.trim() || 'Event Manager';

      const options: EmailTemplateOptions = {
        eventName: this.eventData.eventName || 'Event',
        eventManager: eventManager,
        eventStartDate: this.eventData.eventStartDate,
        eventEndDate: this.eventData.eventEndDate,
        eventTime: this.eventData.eventTime || 'Time not set',
        location: this.eventData.location || 'Location not set',
        eventTypeName: this.eventData.eventTypeName || 'Program',
        managers: this.eventData.managers || [],
        eventLink: eventLink,
      };
      const updatedTemplate = this.studentShare
        ? this.getStudentShareTemplate(options)
        : this.getFallbackTemplate(options);

      this.emailPreviewControl.setValue(updatedTemplate);
      if (this.emailEditor) {
        this.emailEditor.nativeElement.innerHTML = updatedTemplate;
      }
    } else {
      const currentTemplate = this.emailPreviewControl.value;
      if (currentTemplate) {
        const updatedTemplate = currentTemplate.replace(
          '[Event Link Will Appear Here]',
          eventLink,
        );
        this.emailPreviewControl.setValue(updatedTemplate);
        if (this.emailEditor) {
          this.emailEditor.nativeElement.innerHTML = updatedTemplate;
        }
      }
    }
  }

  onEditorInput(event: Event): void {
    const content = this.emailEditor?.nativeElement?.innerHTML;
    if (content !== undefined) {
      this.form.patchValue({ emailPreview: content }, { emitEvent: false });
    }
  }

  onEditorBlur(): void {
    const content = this.emailEditor?.nativeElement?.innerHTML;
    if (content !== undefined) {
      this.form.patchValue({ emailPreview: content }, { emitEvent: false });
    }
  }

  formatText(command: string, value: string = ''): void {
    if (this.emailEditor?.nativeElement) {
      this.emailEditor.nativeElement.focus();
      document.execCommand(command, false, value);
      const content = this.emailEditor.nativeElement.innerHTML;
      this.form.patchValue({ emailPreview: content }, { emitEvent: false });
    }
  }

  copyEmailTemplate(): void {
    const emailContent = this.emailPreviewControl.value;
    if (emailContent) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([emailContent], { type: 'text/html' }),
        'text/plain': new Blob([this.htmlToPlainText(emailContent)], {
          type: 'text/plain',
        }),
      });

      navigator.clipboard
        .write([clipboardItem])
        .then(() => {
          this.showSuccessToast('Email template copied to clipboard');
        })
        .catch(() => {
          navigator.clipboard
            .writeText(this.htmlToPlainText(emailContent))
            .then(() => {
              this.showSuccessToast('Email template copied to clipboard');
            })
            .catch(() => {
              this.showErrorToast('Failed to copy email template');
            });
        });
    } else {
      this.showErrorToast('No email template available to copy');
    }
  }

  private htmlToPlainText(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  copyEventLink(): void {
    if (this.generatedEventLink) {
      navigator.clipboard
        .writeText(this.generatedEventLink)
        .then(() => {
          this.showSuccessToast('Event invitation link copied to clipboard');
        })
        .catch(() => {
          this.showErrorToast('Failed to copy link');
        });
    } else {
      this.showErrorToast('No link available to copy');
    }
  }

  addEmail(): void {
    const emailValue = this.emailInputControl.value?.trim();
    if (!emailValue) return;

    if (!this.isValidEmail(emailValue)) {
      this.showEmailErrorMessage('Please enter a valid email address.');
      return;
    }
    if (this.emailsList.includes(emailValue)) {
      this.showEmailErrorMessage('This email has already been added.');
      return;
    }

    this.emailsList.push(emailValue);
    this.updateFormEmailValue();
    this.emailInputControl.setValue('');
    this.clearEmailError();
  }

  removeEmail(emailToRemove: string): void {
    this.emailsList = this.emailsList.filter(
      (email) => email !== emailToRemove,
    );
    this.updateFormEmailValue();
    this.clearEmailError();
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.emailInputControl.value) {
      if (this.emailsList.length > 0) {
        this.removeEmail(this.emailsList[this.emailsList.length - 1]);
      }
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addEmail();
    }
    if (event.key === ',' || event.key === ';') {
      event.preventDefault();
      this.addEmail();
    }
    if (event.key === ' ') {
      const emailValue = this.emailInputControl.value?.trim();
      if (emailValue && this.isValidEmail(emailValue)) {
        event.preventDefault();
        this.addEmail();
      }
    }
  }

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (value.endsWith(' ')) {
      const emailValue = value.trim();
      if (
        emailValue &&
        this.isValidEmail(emailValue) &&
        !this.emailsList.includes(emailValue)
      ) {
        this.emailsList.push(emailValue);
        this.updateFormEmailValue();
        this.emailInputControl.setValue('', { emitEvent: false });
        this.clearEmailError();
      } else if (emailValue && !this.isValidEmail(emailValue)) {
        this.showEmailErrorMessage('Please enter a valid email address.');
        this.emailInputControl.setValue('', { emitEvent: false });
      } else {
        this.emailInputControl.setValue(value.trim(), { emitEvent: false });
      }
    }
  }

  addEmailOnBlur(): void {
    const emailValue = this.emailInputControl.value?.trim();
    if (
      emailValue &&
      this.isValidEmail(emailValue) &&
      !this.emailsList.includes(emailValue)
    ) {
      this.addEmail();
    } else if (emailValue && !this.isValidEmail(emailValue)) {
      this.showEmailErrorMessage('Please enter a valid email address.');
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  updateFormEmailValue(): void {
    const emailsString = this.emailsList.join(', ');
    this.form.get('email')?.setValue(emailsString);
    if (this.emailsList.length > 0) {
      this.form.get('email')?.setErrors(null);
    } else {
      this.form.get('email')?.setErrors({ required: true });
    }
  }

  showEmailErrorMessage(message: string): void {
    this.emailErrorMessage = message;
    this.showEmailError = true;
  }

  clearEmailError(): void {
    this.showEmailError = false;
    this.emailErrorMessage = '';
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSend(): void {
    if (this.emailsList.length === 0) {
      this.showEmailErrorMessage('Please enter a recipient.');
      this.form.get('email')?.markAsTouched();
      return;
    }
    const invalidEmails = this.emailsList.filter(
      (email) => !this.isValidEmail(email),
    );
    if (invalidEmails.length > 0) {
      this.showEmailErrorMessage(
        `Invalid email(s): ${invalidEmails.join(', ')}`,
      );
      return;
    }
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;
    
    // Get the edited content from the editor
    let emailBody = '';
    if (this.emailEditor && this.emailEditor.nativeElement) {
      emailBody = this.emailEditor.nativeElement.innerHTML;
      this.form.patchValue({ emailPreview: emailBody });
    } else {
      emailBody = this.emailPreviewControl.value;
    }

    // Send the edited content directly without modifying it
    const formData = new FormData();
    formData.append('body', emailBody);
    formData.append(
      'subject',
      this.studentShare
        ? "You're Invited to an Upcoming Construction Pathways Event"
        : 'Your Upcoming Construction Pathways Confirmation',
    );
    formData.append('to', JSON.stringify(this.emailsList));
    if (this.form.get('ccEmail')?.value) {
      formData.append('ccEmail', this.form.get('ccEmail')?.value);
    }

    this.httpService
      .post<any>(`events/${this.eventId}/send-mail`, formData, true)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.showSuccessToast('Email sent successfully');
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.showErrorToast(error.message || 'Failed to send email');
        },
      });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'success' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-toast'],
    });
  }

  private showErrorToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'error' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-toast'],
    });
  }
}